import { ApiEndpointGroup } from './api-spec.model';

export const API_SPEC: ApiEndpointGroup[] = [
  // ─── Patients ──────────────────────────────────────────────
  {
    id: 'patients',
    title: 'Patients',
    description: 'Access patient records associated with your clinic. Requires the patients:read scope.',
    endpoints: [
      {
        id: 'list-patients',
        method: 'GET',
        path: '/partner/v1/patients',
        summary: 'List Patients',
        description: 'Returns a paginated list of patients. Supports filtering by name or email and sorting by different fields.',
        scope: 'patients:read',
        parameters: [
          { name: 'pageNumber', location: 'query', type: 'integer', required: false, defaultValue: '0', description: 'Zero-based page index.' },
          { name: 'pageSize', location: 'query', type: 'integer', required: false, defaultValue: '10', description: 'Results per page (1–100).', },
          { name: 'orderBy', location: 'query', type: 'string', required: false, defaultValue: 'fullName', description: 'Field to sort by (e.g. fullName, email, birthDate).' },
          { name: 'sortBy', location: 'query', type: 'string', required: false, defaultValue: 'ASC', description: 'Sort direction.', enum: ['ASC', 'DESC'] },
          { name: 'search', location: 'query', type: 'string', required: false, description: 'Filter by patient name or email (partial match).' }
        ],
        responseExample: JSON.stringify({
          data: [
            {
              id: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
              full_name: 'Maria Silva Santos',
              email: 'maria.santos@email.com',
              phone: '11987654321',
              birth_date: '1990-05-15',
              marital_status: 'Solteiro',
              profession: 'Engenheira',
              phone_country_code: '55',
              image_auth: true,
              instagram_account: '@maria_santos',
              is_model_patient: false,
              rg: '123456789',
              cpf: '12345678901',
              is_foreigner: false,
              address: {
                street: 'Rua das Flores',
                number: '123',
                complement: 'Apto 456',
                neighborhood: 'Jardim América',
                city: 'São Paulo',
                state: 'SP',
                country: 'BR',
                zip_code: '01310-100',
                latitude: -23.5505,
                longitude: -46.6333
              },
              module: {
                id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
                description: 'Pacientes'
              },
              guardian_name: null,
              guardian_cpf: null,
              guardian_phone: null,
              guardian_phone_country_code: null
            }
          ],
          page_info: {
            current_page: 0,
            total_pages: 5,
            total_elements: 47,
            elements_per_page: 10
          }
        }, null, 2),
        errorCodes: [
          { status: 401, description: 'Invalid or expired access token.' },
          { status: 403, description: 'Insufficient scope — requires patients:read.' },
          { status: 429, description: 'Rate limit exceeded. Check the Retry-After header.' }
        ],
        curlExample: `curl -X GET "https://server.westetics.com.br/partner/v1/patients?pageNumber=0&pageSize=10&sortBy=ASC" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`
      },
      {
        id: 'get-patient',
        method: 'GET',
        path: '/partner/v1/patients/{id}',
        summary: 'Get Patient by ID',
        description: 'Returns detailed information about a specific patient. The response structure is the same as a single item from the List Patients endpoint.',
        scope: 'patients:read',
        parameters: [
          { name: 'id', location: 'path', type: 'string (UUID)', required: true, description: 'The patient unique identifier.' }
        ],
        responseExample: JSON.stringify({
          id: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
          full_name: 'Maria Silva Santos',
          email: 'maria.santos@email.com',
          phone: '11987654321',
          birth_date: '1990-05-15',
          marital_status: 'Solteiro',
          profession: 'Engenheira',
          phone_country_code: '55',
          image_auth: true,
          instagram_account: '@maria_santos',
          is_model_patient: false,
          rg: '123456789',
          cpf: '12345678901',
          is_foreigner: false,
          address: {
            street: 'Rua das Flores',
            number: '123',
            complement: 'Apto 456',
            neighborhood: 'Jardim América',
            city: 'São Paulo',
            state: 'SP',
            country: 'BR',
            zip_code: '01310-100',
            latitude: -23.5505,
            longitude: -46.6333
          },
          module: {
            id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
            description: 'Pacientes'
          },
          guardian_name: null,
          guardian_cpf: null,
          guardian_phone: null,
          guardian_phone_country_code: null
        }, null, 2),
        errorCodes: [
          { status: 401, description: 'Invalid or expired access token.' },
          { status: 403, description: 'Insufficient scope — requires patients:read.' },
          { status: 404, description: 'Patient not found with the given ID.' },
          { status: 429, description: 'Rate limit exceeded.' }
        ],
        curlExample: `curl -X GET https://server.westetics.com.br/partner/v1/patients/f47ac10b-58cc-4372-a567-0e02b2c3d479 \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`
      }
    ]
  },

  // ─── Anamneses ─────────────────────────────────────────────
  {
    id: 'anamneses',
    title: 'Anamneses',
    description: 'Access patient anamnesis (health history) records. Requires the anamnesis:read scope.',
    endpoints: [
      {
        id: 'list-anamneses',
        method: 'GET',
        path: '/partner/v1/anamneses',
        summary: 'List Anamneses',
        description: 'Returns a paginated list of anamneses. Supports filtering by patient, answer status, date range, and module.',
        scope: 'anamnesis:read',
        parameters: [
          { name: 'pageNumber', location: 'query', type: 'integer', required: false, defaultValue: '0', description: 'Zero-based page index.' },
          { name: 'pageSize', location: 'query', type: 'integer', required: false, defaultValue: '10', description: 'Results per page (1–100).' },
          { name: 'orderBy', location: 'query', type: 'string', required: false, defaultValue: 'createdAt', description: 'Field to sort by.' },
          { name: 'sortBy', location: 'query', type: 'string', required: false, defaultValue: 'ASC', description: 'Sort direction.', enum: ['ASC', 'DESC'] },
          { name: 'patientId', location: 'query', type: 'string', required: false, description: 'Filter by patient UUID.' },
          { name: 'onlyNotAnswered', location: 'query', type: 'boolean', required: false, defaultValue: 'false', description: 'Show only incomplete (unanswered) forms.' },
          { name: 'filterType', location: 'query', type: 'string', required: false, defaultValue: 'ALL', description: 'Filter by completion status.', enum: ['ALL', 'PENDING', 'COMPLETED'] },
          { name: 'initial_date', location: 'query', type: 'string', required: false, defaultValue: '2022-01-01', description: 'Start date filter (yyyy-MM-dd).' },
          { name: 'final_date', location: 'query', type: 'string', required: false, description: 'End date filter (yyyy-MM-dd). Defaults to today.' },
          { name: 'module', location: 'query', type: 'string', required: false, description: 'Filter by module ID (UUID).' }
        ],
        responseExample: JSON.stringify({
          data: [
            {
              id: 'b2c3d4e5-f6a7-8901-bcde-f23456789012',
              created_at: '2026-03-10T14:30:00',
              is_answered: true,
              patient_id: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
              reuse_info: true,
              patient: {
                id: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
                full_name: 'Maria Silva Santos',
                email: 'maria.santos@email.com',
                phone: '11987654321'
              },
              patient_name: 'Maria Silva Santos',
              patient_email: 'maria.santos@email.com',
              patient_phone_number: '+5511987654321',
              url_link: 'https://app.westetics.com.br/anamneses/b2c3d4e5/form',
              answers: [
                {
                  question_id: 'q1-uuid-1234',
                  answer: 'Sim',
                  observation: 'Sem complicações'
                },
                {
                  question_id: 'q2-uuid-5678',
                  answer: 'Não',
                  observation: null
                }
              ],
              version: 3
            }
          ],
          page_info: {
            current_page: 0,
            total_pages: 3,
            total_elements: 28,
            elements_per_page: 10
          }
        }, null, 2),
        errorCodes: [
          { status: 401, description: 'Invalid or expired access token.' },
          { status: 403, description: 'Insufficient scope — requires anamnesis:read.' },
          { status: 429, description: 'Rate limit exceeded.' }
        ],
        curlExample: `curl -X GET "https://server.westetics.com.br/partner/v1/anamneses?pageNumber=0&pageSize=10&filterType=ALL" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`
      },
      {
        id: 'get-anamnesis',
        method: 'GET',
        path: '/partner/v1/anamneses/{id}',
        summary: 'Get Anamnesis by ID',
        description: 'Returns detailed information about a specific anamnesis, including all question answers and the associated patient data.',
        scope: 'anamnesis:read',
        parameters: [
          { name: 'id', location: 'path', type: 'string (UUID)', required: true, description: 'The anamnesis unique identifier.' }
        ],
        responseExample: JSON.stringify({
          id: 'b2c3d4e5-f6a7-8901-bcde-f23456789012',
          created_at: '2026-03-10T14:30:00',
          is_answered: true,
          patient_id: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
          reuse_info: true,
          patient: {
            id: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
            full_name: 'Maria Silva Santos',
            email: 'maria.santos@email.com',
            phone: '11987654321'
          },
          patient_name: 'Maria Silva Santos',
          patient_email: 'maria.santos@email.com',
          patient_phone_number: '+5511987654321',
          url_link: 'https://app.westetics.com.br/anamneses/b2c3d4e5/form',
          answers: [
            {
              question_id: 'q1-uuid-1234',
              answer: 'Sim',
              observation: 'Sem complicações'
            },
            {
              question_id: 'q2-uuid-5678',
              answer: 'Não',
              observation: null
            }
          ],
          version: 3
        }, null, 2),
        errorCodes: [
          { status: 401, description: 'Invalid or expired access token.' },
          { status: 403, description: 'Insufficient scope — requires anamnesis:read.' },
          { status: 404, description: 'Anamnesis not found with the given ID.' },
          { status: 429, description: 'Rate limit exceeded.' }
        ],
        curlExample: `curl -X GET https://server.westetics.com.br/partner/v1/anamneses/b2c3d4e5-f6a7-8901-bcde-f23456789012 \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`
      }
    ]
  },

  // ─── Treatment Plans ───────────────────────────────────────
  {
    id: 'treatment-plans',
    title: 'Treatment Plans (under construction)',
    description: 'Access treatment plan records including procedures, products, and clinical photos. Requires the treatment_plans:read scope.',
    endpoints: [
      {
        id: 'list-treatment-plans',
        method: 'GET',
        path: '/partner/v1/treatment-plans',
        summary: 'List Treatment Plans',
        description: 'Returns a paginated list of treatment plans. Supports searching by patient name or procedure.',
        scope: 'treatment_plans:read',
        underConstruction: true,
        parameters: [
          { name: 'pageNumber', location: 'query', type: 'integer', required: false, defaultValue: '0', description: 'Zero-based page index.' },
          { name: 'pageSize', location: 'query', type: 'integer', required: false, defaultValue: '10', description: 'Results per page (1–100).' },
          { name: 'search', location: 'query', type: 'string', required: false, description: 'Search by patient name or procedure description.' }
        ],
        responseExample: JSON.stringify({
          data: [
            {
              id: 'c3d4e5f6-a7b8-9012-cdef-345678901234',
              observations: 'Tratamento facial completo com botox e preenchimento.',
              status: 'PUBLISHED',
              created_at: '2026-03-05T10:00:00',
              updated_at: '2026-03-10T15:30:00',
              published_at: '2026-03-09T16:45:00',
              answer_group_id: 'ag-uuid-1234',
              client_id: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
              clinic_id: '12345678000190',
              products: [
                {
                  id: 'prod-uuid-001',
                  application_date: '2026-03-15',
                  expiration_date: '2027-03-15',
                  manufacturer: 'Allergan',
                  product_name: 'Botox',
                  product_order: 1,
                  application_regions: [
                    { region: 'Testa', description: 'Aplicar 20 unidades para rugas dinâmicas' }
                  ],
                  photos: []
                }
              ],
              photos: [
                {
                  id: 'photo-uuid-001',
                  photo: 'data:image/jpeg;base64,/9j/4AAQSkZJRg...(truncated)',
                  photo_id: 'ext-photo-001',
                  storage_photo_order: 1,
                  type: 'BEFORE',
                  name: 'Foto Antes',
                  content_type: 'image/jpeg',
                  created_at: '2026-03-05T10:00:00'
                }
              ],
              patient_name: 'Maria Silva Santos',
              patient_age: '35',
              authorize_image_use: true,
              contracted_procedures: 'Botox, Preenchimento Labial',
              responsible_professional: 'Dra. Ana Oliveira',
              total_value: '2500.00',
              is_model_patient: false
            }
          ],
          page_info: {
            current_page: 0,
            total_pages: 2,
            total_elements: 15,
            elements_per_page: 10
          }
        }, null, 2),
        errorCodes: [
          { status: 401, description: 'Invalid or expired access token.' },
          { status: 403, description: 'Insufficient scope — requires treatment_plans:read.' },
          { status: 429, description: 'Rate limit exceeded.' }
        ],
        curlExample: `curl -X GET "https://server.westetics.com.br/partner/v1/treatment-plans?pageNumber=0&pageSize=10" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`
      },
      {
        id: 'get-treatment-plan',
        method: 'GET',
        path: '/partner/v1/treatment-plans/{id}',
        summary: 'Get Treatment Plan by ID',
        description: 'Returns detailed information about a specific treatment plan, including all products, application regions, and clinical photos.',
        scope: 'treatment_plans:read',
        underConstruction: true,
        parameters: [
          { name: 'id', location: 'path', type: 'string (UUID)', required: true, description: 'The treatment plan unique identifier.' }
        ],
        responseExample: JSON.stringify({
          id: 'c3d4e5f6-a7b8-9012-cdef-345678901234',
          observations: 'Tratamento facial completo com botox e preenchimento.',
          status: 'PUBLISHED',
          created_at: '2026-03-05T10:00:00',
          updated_at: '2026-03-10T15:30:00',
          published_at: '2026-03-09T16:45:00',
          answer_group_id: 'ag-uuid-1234',
          client_id: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
          clinic_id: '12345678000190',
          products: [
            {
              id: 'prod-uuid-001',
              application_date: '2026-03-15',
              expiration_date: '2027-03-15',
              manufacturer: 'Allergan',
              product_name: 'Botox',
              product_order: 1,
              application_regions: [
                { region: 'Testa', description: 'Aplicar 20 unidades para rugas dinâmicas' }
              ],
              photos: []
            }
          ],
          photos: [
            {
              id: 'photo-uuid-001',
              photo: 'data:image/jpeg;base64,/9j/4AAQSkZJRg...(truncated)',
              photo_id: 'ext-photo-001',
              storage_photo_order: 1,
              type: 'BEFORE',
              name: 'Foto Antes',
              content_type: 'image/jpeg',
              created_at: '2026-03-05T10:00:00'
            }
          ],
          patient_name: 'Maria Silva Santos',
          patient_age: '35',
          authorize_image_use: true,
          contracted_procedures: 'Botox, Preenchimento Labial',
          responsible_professional: 'Dra. Ana Oliveira',
          total_value: '2500.00',
          is_model_patient: false
        }, null, 2),
        errorCodes: [
          { status: 401, description: 'Invalid or expired access token.' },
          { status: 403, description: 'Insufficient scope — requires treatment_plans:read.' },
          { status: 404, description: 'Treatment plan not found with the given ID.' },
          { status: 429, description: 'Rate limit exceeded.' }
        ],
        curlExample: `curl -X GET https://server.westetics.com.br/partner/v1/treatment-plans/c3d4e5f6-a7b8-9012-cdef-345678901234 \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`
      }
    ]
  },

  // ─── Sales ─────────────────────────────────────────────────
  {
    id: 'sales',
    title: 'Sales',
    description: 'Access sales and contract records. Requires the sales:read scope. Date range filtering is required.',
    endpoints: [
      {
        id: 'list-sales',
        method: 'GET',
        path: '/partner/v1/sales',
        summary: 'List Sales',
        description: 'Returns a paginated list of sales within a date range. Both start_date and end_date are required parameters.',
        scope: 'sales:read',
        parameters: [
          { name: 'start_date', location: 'query', type: 'string', required: true, description: 'Inclusive start date (yyyy-MM-dd). Required.' },
          { name: 'end_date', location: 'query', type: 'string', required: true, description: 'Inclusive end date (yyyy-MM-dd). Required.' },
          { name: 'page', location: 'query', type: 'integer', required: false, defaultValue: '0', description: 'Zero-based page number.' },
          { name: 'size', location: 'query', type: 'integer', required: false, defaultValue: '20', description: 'Results per page (1–100).' }
        ],
        responseExample: JSON.stringify({
          data: [
            {
              id: 'd4e5f6a7-b8c9-0123-def4-567890123456',
              external_id: 'aut-doc-98765',
              signing_date: '2026-03-10T14:30:00',
              created_at: '2026-03-10T14:00:00',
              patient_name: 'Maria Silva Santos',
              patient_phone: '11987654321',
              patient_email: 'maria.santos@email.com',
              patient_birth_date: '1990-05-15',
              patient_cpf: '12345678901',
              patient_rg: '123456789',
              address: {
                street: 'Rua das Flores',
                number: '123',
                complement: 'Apto 456',
                neighborhood: 'Jardim América',
                city: 'São Paulo',
                state: 'SP',
                zip_code: '01310-100'
              },
              procedures: 'Botox, Preenchimento Labial',
              total_value: 2500.50,
              payment_method: 'Cartão de Crédito',
              professional_name: 'Dra. Ana Oliveira',
              contract_status: 'Assinado'
            }
          ],
          page_info: {
            current_page: 0,
            total_pages: 3,
            total_elements: 52,
            elements_per_page: 20
          }
        }, null, 2),
        errorCodes: [
          { status: 400, description: 'Missing or invalid date parameters. Both start_date and end_date are required.' },
          { status: 401, description: 'Invalid or expired access token.' },
          { status: 403, description: 'Insufficient scope — requires sales:read.' },
          { status: 429, description: 'Rate limit exceeded.' }
        ],
        curlExample: `curl -X GET "https://server.westetics.com.br/partner/v1/sales?start_date=2026-03-01&end_date=2026-03-31&page=0&size=20" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`
      },
      {
        id: 'get-sale',
        method: 'GET',
        path: '/partner/v1/sales/{id}',
        summary: 'Get Sale by ID',
        description: 'Returns detailed information about a specific sale, including patient data, procedures, and contract status.',
        scope: 'sales:read',
        parameters: [
          { name: 'id', location: 'path', type: 'string (UUID)', required: true, description: 'The sale unique identifier.' }
        ],
        responseExample: JSON.stringify({
          id: 'd4e5f6a7-b8c9-0123-def4-567890123456',
          external_id: 'aut-doc-98765',
          signing_date: '2026-03-10T14:30:00',
          created_at: '2026-03-10T14:00:00',
          patient_name: 'Maria Silva Santos',
          patient_phone: '11987654321',
          patient_email: 'maria.santos@email.com',
          patient_birth_date: '1990-05-15',
          patient_cpf: '12345678901',
          patient_rg: '123456789',
          address: {
            street: 'Rua das Flores',
            number: '123',
            complement: 'Apto 456',
            neighborhood: 'Jardim América',
            city: 'São Paulo',
            state: 'SP',
            zip_code: '01310-100'
          },
          procedures: 'Botox, Preenchimento Labial',
          total_value: 2500.50,
          payment_method: 'Cartão de Crédito',
          professional_name: 'Dra. Ana Oliveira',
          contract_status: 'Assinado'
        }, null, 2),
        errorCodes: [
          { status: 401, description: 'Invalid or expired access token.' },
          { status: 403, description: 'Insufficient scope — requires sales:read.' },
          { status: 404, description: 'Sale not found with the given ID.' },
          { status: 429, description: 'Rate limit exceeded.' }
        ],
        curlExample: `curl -X GET https://server.westetics.com.br/partner/v1/sales/d4e5f6a7-b8c9-0123-def4-567890123456 \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`
      }
    ]
  }
];
